import React, { FC } from "react";
import { SketchPicker } from "react-color";
import { useImmer } from "use-immer";

import { ColorTextPrimary } from "@intelligems/ig-theme";
import { Box, FormControl, InputLabel } from "@mui/material";
import { styled } from "@mui/material/styles";

const PREFIX = "ColorPicker";

const classes = {
  flexDisplay: `${PREFIX}-flexDisplay`,
  swatch: `${PREFIX}-swatch`,
  offClickDiv: `${PREFIX}-offClickDiv`,
  colorPicker: `${PREFIX}-colorPicker`,
};

const StyledInputLabel = styled(InputLabel)(({ theme }) => ({
  color: "black",
}));

const StyledInput = styled(Box)(({ theme }) => ({
  [`&.${classes.swatch}`]: {
    width: "129px",
    height: "43px",
    borderRadius: 4,
    marginTop: "24px",
    cursor: "pointer",
    border: "1px solid #CBCBCB",
  },
}));

const StyledColorPicker = styled(Box)(({ theme }) => ({
  [`&.${classes.colorPicker}`]: {
    position: "absolute",
    boxShadow: "#CBCBCB 0px 0px 0px",
    zIndex: 2,
  },

  [`& .${classes.offClickDiv}`]: {
    position: "fixed",
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
  },
}));

interface IgColorPickerProps {
  label: string;
  value: string;
  onChange: (e: string) => void;
  disabled?: boolean;
  shouldAlignLeft?: boolean;
}

/**
 * @see FormControl
 * @see SketchPicker
 */
export const IgColorPicker: FC<IgColorPickerProps> = ({
  label,
  value,
  onChange,
  disabled,
  shouldAlignLeft,
}: IgColorPickerProps) => {
  const [colorPickerOpen, setColorPickerOpen] = useImmer<boolean>(false);

  const handlePickerClick = () => {
    setColorPickerOpen(!colorPickerOpen);
  };

  return (
    <>
      <FormControl variant="standard" sx={{ height: "69px" }}>
        <StyledInputLabel
          sx={{ fontWeight: 600, color: ColorTextPrimary }}
          shrink
        >
          {label}
        </StyledInputLabel>
        <StyledInput
          sx={{
            backgroundColor: value,
            border: `1px solid ${
              value ? (value === "000000" ? "#CBCBCB" : value) : "#CBCBCB"
            }`,
          }}
          className={classes.swatch}
          onClick={!disabled ? handlePickerClick : undefined}
        />
      </FormControl>
      {colorPickerOpen && (
        <StyledColorPicker className={classes.colorPicker}>
          <Box
            className={classes.offClickDiv}
            onClick={() => {
              setColorPickerOpen(false);
            }}
          />
          <div
            style={{
              position: "absolute",
              left: shouldAlignLeft ? 0 : "-90px",
              top: "10px",
            }}
          >
            <SketchPicker
              color={value}
              onChange={(color) => onChange(color.hex)}
            />
          </div>
        </StyledColorPicker>
      )}
    </>
  );
};
